import { useMemo } from 'react';

import sortBy from 'lodash/sortBy';

import {
  ListItem,
  ListItemProps,
  Select,
  Typography,
  Box,
  tokens,
} from '@cast/design-system';
import { useSearchContext, useSearchCriterion } from '@cast/shared-search-ui';
import { Cluster, ClusterDisplayStatus } from '@cast/types';

import { ClusterStatusChip } from '@cast/features-cluster-ui';

import { CLUSTER_STATUS_FILTER_GROUP_LABEL } from '../../constants/clusterStatusFilterGroup';
import {
  ClusterStatusFilterGroup,
  deriveClusterStatusFilterGroup,
} from '../../utils/clusterStatusFilterGroup';

type StatusOption = {
  group: ClusterStatusFilterGroup;
  value: ClusterDisplayStatus;
  count: number;
};

type ClusterStateFilterProps = {
  fullWidth?: boolean;
};

export const ClusterStateFilter = ({ fullWidth }: ClusterStateFilterProps) => {
  const searchCtx = useSearchContext<Cluster>();
  const criterion = useSearchCriterion('status');
  const clusterData = searchCtx?.data;

  const statuses: StatusOption[] = useMemo(() => {
    if (!clusterData?.length) {
      return [];
    }

    const statusesMap: Record<string, StatusOption> = {};
    clusterData.forEach((cluster) => {
      const status: Pick<StatusOption, 'group' | 'value'> = {
        group: deriveClusterStatusFilterGroup(cluster),
        value: cluster.displayStatus,
      };
      const key = status.group + status.value;
      const count = (statusesMap[key]?.count ?? 0) + 1;
      statusesMap[key] = {
        ...status,
        count,
      };
    });

    return Object.values(statusesMap);
  }, [clusterData]);

  return (
    <Box sx={fullWidth ? { width: '100%' } : undefined}>
      <Select
        size="md"
        sx={{ width: '100%' }}
        inputText="Cluster status"
        checkable
        multiple
        value={criterion.value}
        onChange={(values: StatusOption[]) => {
          if (values?.length) {
            criterion.setValue?.(values);
          } else {
            criterion.clear?.();
          }
        }}
        options={sortBy(statuses, 'group')}
        groupBy={(option: StatusOption) =>
          CLUSTER_STATUS_FILTER_GROUP_LABEL[option.group]
        }
        optionLabel={(option: StatusOption) =>
          `${CLUSTER_STATUS_FILTER_GROUP_LABEL[option.group]}|${option.value}`
        }
        renderOption={(option: StatusOption, itemProps: ListItemProps) => (
          <ListItem {...itemProps}>
            <Box
              display="flex"
              gap="8px"
              width="100%"
              alignItems="center"
              justifyContent="space-between"
            >
              <ClusterStatusChip
                status={option.value}
                managed={option.group !== 'read_only'}
                noCaption
                noIcon
                sx={{ pointerEvents: 'none' }}
                size="small"
              />
              <Typography
                variant="P12R"
                color={tokens.theme.fg.structural.secondary}
                width="3ch"
                textAlign="end"
              >
                {option.count}
              </Typography>
            </Box>
          </ListItem>
        )}
        isOptionEqualToValue={(left, right) =>
          left.group === right.group && left.value === right.value
        }
        testId="clusters-state-filter"
      />
    </Box>
  );
};
